import { TeamDto, UserTeamDto } from '@act/shared/data-transfer-objects';
import { Button, SelectOption } from '@act/storybook';
import {
  Box,
  Card,
  Checkbox,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { TeamRole } from '@act/shared/types';
import { Info } from '@mui/icons-material';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTime } from 'luxon';
type ManageTeamProps = {
  selectedTeams: UserTeamDto[];
  teamOptions: TeamDto[];
  roleOptions: SelectOption<number>[];
  handleTeamsUpdated: (teams: UserTeamDto[]) => void;
  timezone?: string;
};

const roleInfoDialogConfig = {
  title: 'Team Roles',
  message: (
    <div>
      <Typography>
        <b>Member: </b>Can view and complete tasks for a team, contributing to
        the team's quotas. A user can be a member of only one team.
      </Typography>
      <br />
      <Typography>
        <b>Viewer: </b>Can access member information for a team but cannot
        contribute to tasks or quotas. A user can have viewer permissions for
        multiple teams.
      </Typography>
    </div>
  ),
  hideActions: true,
  hideIcon: true,
  showClose: true,
};

const ManageTeams = ({
  selectedTeams,
  teamOptions,
  roleOptions,
  handleTeamsUpdated,
  timezone,
}: ManageTeamProps) => {
  const [selectedTeamId, setSelectedTeamId] = useState<number | ''>('');
  const [selectedRole, setSelectedRole] = useState<number | ''>('');
  const [isManager, setIsManager] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const showTeamRoleInfoDialog = useConfirmationAlertDialog();

  const roleError =
    selectedRole === TeamRole.MEMBER &&
    selectedTeams?.filter(team => team.role === TeamRole.MEMBER).length > 0
      ? 'User may only be MEMBER for one team'
      : null;

  const submitDisabled = !selectedTeamId || !selectedRole;

  const resetFields = () => {
    setIsManager(false);
    setSelectedTeamId('');
    setSelectedRole('');
  };

  const removeTeam = (teamId: number) => {
    const filteredTeams = selectedTeams.filter(team => team.teamId !== teamId);
    handleTeamsUpdated(filteredTeams);
  };

  const addTeam = () => {
    if (selectedTeamId && selectedRole) {
      const userTeamDto: UserTeamDto = {
        teamId: selectedTeamId,
        role: selectedRole,
        isManager: isManager,
        // Start and end date based on timezone or default to earliest time zone we have or latest for end date
        startDate: DateTime.fromJSDate(startDate)
          .setZone(timezone || 'America/New_York')
          .startOf('day')
          .toMillis(),
        endDate: DateTime.fromJSDate(endDate)
          .setZone(timezone || 'America/Los_Angeles')
          .startOf('day')
          .toMillis(),
      };
      const team = selectedTeams.filter(team => team.teamId === selectedTeamId);
      if (team.length === 0) {
        handleTeamsUpdated([userTeamDto, ...selectedTeams]);
      }
      setStartDate(null);
      setEndDate(null);
      resetFields();
    }
  };

  const teamPicker = (
    <Select
      sx={{ minWidth: '200px' }}
      value={selectedTeamId}
      onChange={e => setSelectedTeamId(e.target.value as number)}
    >
      {teamOptions?.map(option => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </Select>
  );

  const rolePicker = (
    <div style={{ flexDirection: 'column' }}>
      {roleError && (
        <Typography variant="body2" sx={{ color: 'red' }}>
          {roleError}
        </Typography>
      )}
      <Select
        sx={{ minWidth: '200px' }}
        value={selectedRole}
        onChange={event => setSelectedRole(event.target.value as number)}
      >
        {roleOptions?.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </div>
  );

  const roleInfoHeader = (
    <Typography
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
      }}
    >
      Role
      <a
        style={{ lineHeight: 0 }}
        onClick={() => showTeamRoleInfoDialog(roleInfoDialogConfig)}
      >
        <Info style={{ paddingLeft: '4px' }} />
      </a>
    </Typography>
  );

  return (
    <div style={{ flex: 1 }}>
      <Typography variant="h6">Teams</Typography>
      <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }} aria-label="team management table">
          <TableHead>
            <TableRow>
              <TableCell>Team Name</TableCell>
              <TableCell align="center">{roleInfoHeader}</TableCell>
              <TableCell align="center">Is Manager</TableCell>
              <TableCell align="center">Start Date</TableCell>
              <TableCell align="center">End Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedTeams?.map(row => {
              const teamConfig = teamOptions?.find(t => t.id === row.teamId);
              return (
                <TableRow
                  key={row.teamId}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {teamConfig?.name}
                  </TableCell>
                  <TableCell align="center">{TeamRole[row.role]}</TableCell>
                  <TableCell align="center">
                    {row.isManager ? 'True' : 'False'}
                  </TableCell>
                  <TableCell align="center">
                    {row.startDate
                      ? DateTime.fromMillis(row.startDate).toLocaleString(
                          DateTime.DATETIME_MED
                        )
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {row.endDate
                      ? DateTime.fromMillis(row.endDate).toLocaleString(
                          DateTime.DATETIME_MED
                        )
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    <a
                      onClick={() => removeTeam(row.teamId)}
                      style={{ color: 'red' }}
                    >
                      Remove
                    </a>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {teamPicker}
              </TableCell>
              <TableCell align="center">{rolePicker}</TableCell>
              <TableCell align="center">
                <Checkbox
                  checked={isManager}
                  onClick={() => setIsManager(!isManager)}
                />
              </TableCell>
              <TableCell align="center">
                <Box sx={{ minWidth: 160, width: '100%' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                    />
                  </LocalizationProvider>
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ minWidth: 160, width: '100%' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={endDate}
                      onChange={setEndDate}
                      label="End Date"
                    />
                  </LocalizationProvider>
                </Box>
              </TableCell>
              <TableCell align="center">
                <Button
                  debounced
                  disabled={!!roleError || submitDisabled}
                  onClick={addTeam}
                >
                  Add Team
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ManageTeams;
